/**
 * @fileoverview Client-side PDF Letter-vs-Form detection.
 *
 * Purpose:
 * - Inspect a PDF in the browser before upload to suggest whether it should be
 *   treated as a third-party fillable form or a plain letter template.
 * - Used by the precedent library bulk-upload wizard to pre-select the Type
 *   column so the user doesn't have to toggle each row manually.
 *
 * How it decides:
 * - Loads the PDF via `pdf-lib`. If `getForm().getFields().length > 0`,
 *   returns `"form"` (it has interactive AcroForm widgets).
 * - Otherwise — including the unparseable / encrypted / XFA-only cases —
 *   returns `"letter"` and lets the user override.
 *
 * Note: server-side extraction (`convex/lib/xfaHelpers.ts`) handles XFA forms
 * authoritatively; this client probe only inspects standard AcroForm widgets,
 * which covers the overwhelming majority of fillable PDFs uploaded by firms.
 */

import { PDFDocument } from "pdf-lib";

export type DetectedTemplateType = "letter" | "form";

/**
 * Probe a PDF File and suggest a template type.
 *
 * Returns `"form"` only when AcroForm fields are present; defaults to
 * `"letter"` otherwise (including on parse errors).
 */
export async function detectPdfFormType(
  file: File,
): Promise<DetectedTemplateType> {
  try {
    const buffer = await file.arrayBuffer();
    const pdf = await PDFDocument.load(buffer, {
      ignoreEncryption: true,
      throwOnInvalidObject: false,
    });
    const fields = pdf.getForm().getFields();
    return fields.length > 0 ? "form" : "letter";
  } catch {
    return "letter";
  }
}
